import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Zap } from "lucide-react";

const DAYS = [
  { code: "lunes", label: "Lunes" },
  { code: "martes", label: "Martes" },
  { code: "miercoles", label: "Miércoles" },
  { code: "jueves", label: "Jueves" },
  { code: "viernes", label: "Viernes" },
  { code: "sabado", label: "Sábado" },
  { code: "domingo", label: "Domingo" },
];

const QUICK_FILL = {
  lunes: { start: "07:00", end: "17:00" },
  martes: { start: "07:00", end: "17:00" },
  miercoles: { start: "07:00", end: "17:00" },
  jueves: { start: "07:00", end: "17:00" },
  viernes: { start: "07:00", end: "17:00" },
  sabado: { start: "07:00", end: "13:00" },
};

const DEFAULT_TIME = { start: "07:00", end: "17:00" };

// Horas ordinarias máximas por día según el tipo de jornada (Código de Trabajo CR).
const DAILY_ORD_LIMIT = { JOD: 8, JOM: 7, JON: 6, JOA: 8, JOE: 8 };
// Tope semanal de horas ordinarias por ley según tipo de jornada.
const WEEKLY_ORD_LIMIT = { JOD: 48, JOM: 42, JON: 36, JOA: 48, JOE: 48 };

const parseHHMM = (s) => {
  if (!s) return null;
  const [h, m] = String(s).split(":");
  const v = Number(h) * 60 + Number(m || 0);
  return Number.isFinite(v) ? v : null;
};

// Devuelve { ordinary, extra } en horas para un bloque start→end y un tope diario.
export const dayHours = (start, end, limit = 8) => {
  const a = parseHHMM(start);
  const b = parseHHMM(end);
  if (a === null || b === null) return { ordinary: 0, extra: 0, total: 0 };
  let dur = b - a;
  if (dur < 0) dur += 24 * 60; // cruza medianoche
  const limMin = limit * 60;
  const ordinary = Math.min(dur, limMin) / 60;
  const extra = Math.max(0, dur - limMin) / 60;
  return { ordinary, extra, total: dur / 60 };
};

const fmtH = (h) => {
  if (!h) return "0";
  return Number.isInteger(h) ? String(h) : h.toFixed(1).replace(/\.0$/, "");
};

// Unifica la selección de días laborales con el horario de entrada/salida por día,
// muestra horas ordinarias/extra por día y total semanal, e incluye una casilla de
// aceptación voluntaria de la jornada.
// onChange recibe { work_days, day_schedules, days_off }.
export function WorkScheduleEditor({
  workDays = [],
  daySchedules = {},
  weekDays,
  onChange,
  journeyType = "JOD",
  accepted = false,
  onAcceptedChange,
}) {
  const days = weekDays && weekDays.length ? weekDays : DAYS;
  const selected = new Set(workDays);
  const limit = DAILY_ORD_LIMIT[journeyType] || 8;
  const weeklyLimit = WEEKLY_ORD_LIMIT[journeyType] || 48;

  const emit = (nextSet, nextSched) => {
    const work_days = days.map((d) => d.code).filter((c) => nextSet.has(c));
    const day_schedules = {};
    work_days.forEach((c) => {
      day_schedules[c] = nextSched[c] || { ...DEFAULT_TIME };
    });
    const days_off = days.map((d) => d.code).filter((c) => !nextSet.has(c));
    onChange({ work_days, day_schedules, days_off });
  };

  const toggle = (code) => {
    const next = new Set(selected);
    const sched = { ...daySchedules };
    if (next.has(code)) {
      next.delete(code);
      delete sched[code];
    } else {
      next.add(code);
      sched[code] = daySchedules[code] || { ...DEFAULT_TIME };
    }
    emit(next, sched);
  };

  const setTime = (code, field, value) => {
    const sched = {
      ...daySchedules,
      [code]: { ...(daySchedules[code] || DEFAULT_TIME), [field]: value },
    };
    emit(new Set(selected), sched);
  };

  const quickFill = () => emit(new Set(Object.keys(QUICK_FILL)), QUICK_FILL);

  const daysOff = days.filter((d) => !selected.has(d.code));

  // Totales semanales: el extra semanal se calcula sobre el TOPE LEGAL semanal
  // (total laborado − tope), no como suma de los extras diarios.
  let weekTotal = 0;
  days.forEach((d) => {
    if (!selected.has(d.code)) return;
    const sch = daySchedules[d.code] || {};
    const { total } = dayHours(sch.start, sch.end, limit);
    weekTotal += total;
  });
  const weekExtra = Math.max(0, weekTotal - weeklyLimit);

  return (
    <div className="space-y-3" data-testid="work-schedule-editor">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium text-zinc-700">
          Días y horario laboral *
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={quickFill}
          className="h-7 gap-1.5 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
          data-testid="schedule-quick-fill-button"
        >
          <Zap className="w-3.5 h-3.5" /> Llenado rápido
        </Button>
      </div>
      <p className="text-[11px] text-zinc-500 -mt-1">
        Marque cada día laboral y defina su hora de entrada y salida. Se muestran las
        horas ordinarias y extraordinarias de cada día (tope ordinario: {limit} h/día).
      </p>

      <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-100 overflow-hidden">
        {days.map((d) => {
          const isOn = selected.has(d.code);
          const sch = daySchedules[d.code] || DEFAULT_TIME;
          const { ordinary, extra } = dayHours(sch.start, sch.end, limit);
          return (
            <div
              key={d.code}
              className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 ${isOn ? "bg-white" : "bg-zinc-50/60"}`}
              data-testid={`schedule-row-${d.code}`}
            >
              <label className="flex items-center gap-2 cursor-pointer w-28 shrink-0">
                <Checkbox
                  checked={isOn}
                  onCheckedChange={() => toggle(d.code)}
                  data-testid={`schedule-workday-${d.code}`}
                />
                <span className={`text-sm ${isOn ? "text-zinc-800 font-medium" : "text-zinc-400"}`}>
                  {d.label}
                </span>
              </label>
              {isOn ? (
                <>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={sch.start || ""}
                      onChange={(e) => setTime(d.code, "start", e.target.value)}
                      className="h-8 w-28 text-xs"
                      data-testid={`schedule-start-${d.code}`}
                    />
                    <span className="text-zinc-400 text-xs">a</span>
                    <Input
                      type="time"
                      value={sch.end || ""}
                      onChange={(e) => setTime(d.code, "end", e.target.value)}
                      className="h-8 w-28 text-xs"
                      data-testid={`schedule-end-${d.code}`}
                    />
                  </div>
                  <span
                    className="text-[11px] text-zinc-600 ml-auto"
                    data-testid={`schedule-hours-${d.code}`}
                  >
                    <span className="text-emerald-700 font-medium">{fmtH(ordinary)}h ord</span>
                    <span className="text-zinc-300 mx-1">·</span>
                    <span className={extra > 0 ? "text-orange-600 font-medium" : "text-zinc-400"}>
                      {fmtH(extra)}h extra
                    </span>
                  </span>
                </>
              ) : (
                <span className="text-xs text-zinc-400 italic">Descanso</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between text-[11px]">
        <span className="text-zinc-500" data-testid="schedule-days-off">
          Día(s) libre(s):{" "}
          <span className="font-medium text-zinc-700">
            {daysOff.length ? daysOff.map((d) => d.label).join(", ") : "Ninguno"}
          </span>
        </span>
        <span className="text-zinc-600" data-testid="schedule-week-totals">
          Semana:{" "}
          <span className="text-zinc-800 font-semibold">{fmtH(weekTotal)}h laboradas</span>
          <span className="text-zinc-300 mx-1">·</span>
          <span className={weekExtra > 0 ? "text-orange-600 font-semibold" : "text-zinc-400"}>
            {fmtH(weekExtra)}h extra
          </span>
          <span className="text-zinc-400"> (tope {weeklyLimit}h)</span>
        </span>
      </div>

      {onAcceptedChange && (
        <label
          className="flex items-start gap-2 rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 cursor-pointer"
          data-testid="schedule-acceptance-label"
        >
          <Checkbox
            checked={!!accepted}
            onCheckedChange={(v) => onAcceptedChange(!!v)}
            className="mt-0.5"
            data-testid="schedule-acceptance-checkbox"
          />
          <span className="text-[11px] leading-relaxed text-zinc-700">
            El trabajador <b>acepta de forma libre y voluntaria</b> este horario y
            jornada laboral, declarando estar <b>consciente</b> de que representa
            aproximadamente <b>{fmtH(weekTotal)} horas laboradas</b> por semana, de las
            cuales <b>{fmtH(weekExtra)} son extraordinarias</b> (lo que excede las{" "}
            {weeklyLimit} horas semanales de ley) y se remunerarán con el recargo del 50%
            del artículo 139 del Código de Trabajo. Esta aceptación se incluirá en el
            contrato laboral.
          </span>
        </label>
      )}
    </div>
  );
}

export default WorkScheduleEditor;
